import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  dismissAllNotifications,
  deleteNotification,
} from '../api/notificationService';
import { extractUserInfo } from '../utils/jwtDecoder';
import websocketService from '../services/websocketService';
import { useWebSocket } from './WebSocketContext';
import { showRealtimeNotificationToast, clearRealtimeNotificationToastQueue } from '../utils/realtimeNotificationToast';
import { registerNotificationLogoutHandlers } from '../services/logoutService';
import { QUEUE_REALTIME_NOTIFICATION_UI_EVENT } from '../services/queueRealtimeNotificationBridge';
import {
  buildNotificationNavigationPayload,
  isBackendPersistedNotificationId,
  registerNotificationClickActions,
} from '../utils/notificationNavigation';
import { emitCustomerTicketActivity } from '../utils/customerTicketActivity';
import {
  PROFILE_COMPLETION_NOTIFICATION_ID,
  buildProfileCompletionNotification,
  dismissProfileCompletionReminderForSession,
  getStoredUserDetails,
  isMobileNumberMissing,
  isProfileCompletionNotification,
  isProfileCompletionReminderDismissed,
} from '../utils/profileCompletionReminder';

// Create notification context
const NotificationContext = createContext(undefined);

/** Wires bell + toast clicks to the same navigate/markAsRead handlers. */
function NotificationNavigationRegistrar() {
  const navigate = useNavigate();
  const { markAsRead } = useContext(NotificationContext);

  useEffect(() => {
    registerNotificationClickActions({
      navigate: (path) => navigate(path),
      markAsRead: (notification) => {
        const displayId = notification?.id ?? notification?.notificationId;
        if (displayId != null && displayId !== '') {
          markAsRead(displayId);
        }
      },
    });
    return () => registerNotificationClickActions({ navigate: null, markAsRead: null });
  }, [navigate, markAsRead]);

  return null;
}

// Storage key for notifications
const NOTIFICATIONS_STORAGE_KEY = 'user_notifications';
const PANEL_MAX_NOTIFICATIONS = 30;
const NOTIFICATION_REALTIME_DEBOUNCE_MS = 700;
const CHAT_NOTIFICATION_TYPES = new Set(['chat_request', 'chat_accepted']);
/** Persisted unassigned pool ticket creation (authoritative; queue TICKET_CREATED synthetic is suppressed). */
const NEW_UNASSIGNED_TICKET_TYPE = 'new_unassigned_ticket';
const UNASSIGNED_TICKET_CREATION_TYPES = new Set(['ticket_created', NEW_UNASSIGNED_TICKET_TYPE]);
/** Aligned with backend notificationType + queue bridge mapQueueEventTypeToNotificationType */
const TOAST_ELIGIBLE_NOTIFICATION_TYPES = new Set([
  'team_transferred',
  'ticket_assigned',
  'ticket_reply',
  'queue_transfer',
  'ticket_team_transferred',
  'ticket_created',
  NEW_UNASSIGNED_TICKET_TYPE,
  'inquiry_auto_escalated_awaiting_assignment',
]);

function isUnreadNotificationRow(n) {
  return Boolean(n) && !n.read && !n.isRead;
}

function notificationTypeKey(n) {
  return String(n?.notificationType || n?.type || '').toLowerCase();
}

function isUnassignedTicketCreationRow(n) {
  const nt = notificationTypeKey(n);
  if (UNASSIGNED_TICKET_CREATION_TYPES.has(nt)) {
    return true;
  }
  const bridge = n?.metadata?.bridgeEventType ?? n?.data?.bridgeEventType;
  return bridge === 'TICKET_CREATED' && !hasConcreteAssigneeFromMetadata(n?.metadata || n?.data || {});
}

function hasConcreteAssigneeFromMetadata(meta) {
  if (!meta || typeof meta !== 'object') {
    return false;
  }
  const assignee =
    meta.destinationAssigneeId ??
    meta.assigneeId ??
    meta.assignedAgentId ??
    meta.assigned_agent_id;
  return assignee != null && String(assignee).trim() !== '';
}

/** One logical alert per ticket for unassigned customer ticket creation. */
function unassignedTicketCreationDedupeKey(ticketId) {
  if (ticketId == null || ticketId === '') {
    return null;
  }
  return `unassigned-ticket-created:${ticketId}`;
}

function preferIncomingUnassignedRow(existing, incoming) {
  const inNt = notificationTypeKey(incoming);
  if (inNt === NEW_UNASSIGNED_TICKET_TYPE) {
    return true;
  }
  const exNt = notificationTypeKey(existing);
  if (exNt === NEW_UNASSIGNED_TICKET_TYPE) {
    return false;
  }
  return isBackendPersistedNotificationId(incoming?.notificationId ?? incoming?.id);
}

function resolveNotificationPersistedId(notification) {
  if (!notification) {
    return null;
  }
  const candidates = [
    notification.notificationId,
    notification.id,
    notification.data?.notificationId,
    notification.metadata?.notificationId,
  ];
  for (const candidate of candidates) {
    if (isBackendPersistedNotificationId(candidate)) {
      return candidate;
    }
  }
  return null;
}

function extractTicketIdFromNotif(n) {
  if (!n) return null;
  const tid =
    n.ticketId ??
    n.metadata?.ticketId ??
    n.data?.ticketId ??
    (typeof n.link === 'string' && /\/agent\/tickets\/(\d+)/.exec(n.link)?.[1]) ??
    (typeof n.link === 'string' && /\/tickets\/(\d+)/.exec(n.link)?.[1]);
  if (tid == null || tid === '') return null;
  const num = typeof tid === 'number' ? tid : Number(String(tid).trim());
  return Number.isFinite(num) ? num : null;
}

function cleanupExpiryMap(map, now) {
  for (const [k, exp] of map.entries()) {
    if (typeof exp === 'number' && exp <= now) {
      map.delete(k);
    }
  }
}

/**
 * Merge API-first with short-lived synthetic `qr:*` rows from the queue bridge until API supersedes them.
 */
function mergeMappedWithQueueSynthetics(prev, mapped, maxSize) {
  const mappedList = Array.isArray(mapped) ? [...mapped] : [];
  const apiQueueEventIds = new Set();
  for (const m of mappedList) {
    const mm = m.metadata || m.data || {};
    if (mm.queueEventId != null && String(mm.queueEventId).trim() !== '') {
      apiQueueEventIds.add(String(mm.queueEventId).trim());
    }
  }

  const synthetics = [];
  for (const n of prev || []) {
    const id = String(n.notificationId || n.id || '');
    if (!id.startsWith('qr:')) {
      continue;
    }
    const sm = n.metadata || {};
    const qe = sm.queueEventId != null ? String(sm.queueEventId).trim() : null;
    if (qe && apiQueueEventIds.has(qe)) {
      continue;
    }
    const stid = extractTicketIdFromNotif(n);
    const sBridge = sm.bridgeEventType;
    const snt = String(n.notificationType || n.type || '').toLowerCase();
    let superseded = false;
    for (const m of mappedList) {
      const mtid = extractTicketIdFromNotif(m);
      if (stid == null || mtid == null || String(stid) !== String(mtid)) {
        continue;
      }
      const mm = m.metadata || m.data || {};
      if (sBridge && mm.eventType && String(mm.eventType) === String(sBridge)) {
        superseded = true;
        break;
      }
      const mnt = String(m.notificationType || m.type || '').toLowerCase();
      if (
        (snt === 'ticket_team_transferred' || snt === 'team_transferred') &&
        (mnt === 'team_transferred' || mnt === 'ticket_team_transferred')
      ) {
        superseded = true;
        break;
      }
      if (snt === 'ticket_assigned' && mnt === 'ticket_assigned') {
        superseded = true;
        break;
      }
      if (snt === 'ticket_created' && (mnt === 'ticket_created' || mnt === 'ticket_update')) {
        superseded = true;
        break;
      }
      if (
        (snt === 'ticket_created' || sBridge === 'TICKET_CREATED') &&
        mnt === NEW_UNASSIGNED_TICKET_TYPE
      ) {
        superseded = true;
        break;
      }
      if (snt === 'queue_transfer' && (mnt === 'queue_transfer' || mnt === 'ticket_update')) {
        superseded = true;
        break;
      }
    }
    if (superseded) {
      continue;
    }
    const createdMs = new Date(n.createdAt || n.timestamp || 0).getTime();
    if (Number.isFinite(createdMs) && Date.now() - createdMs > 4 * 60 * 1000) {
      continue;
    }
    synthetics.push(n);
  }

  const seen = new Set();
  const out = [];
  for (const s of synthetics) {
    const id = String(s.notificationId || s.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(s);
  }
  for (const m of mappedList) {
    const id = String(m.notificationId || m.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(m);
  }
  return out.slice(0, maxSize);
}

/**
 * NotificationProvider - Manages notification state globally
 * Syncs with backend API and stores in localStorage as fallback
 */
export const NotificationProvider = ({ children }) => {
  const { isConnected: wsConnected } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const notificationRefreshTimerRef = useRef(null);
  /** Suppress duplicate API toast shortly after the same ticket+type was toasted via queue bridge */
  const bridgeToastRecentByTicketRef = useRef(new Map());
  /** Dedupe duplicate queue WS deliveries by backend event id */
  const processedQueueBridgeEventIdsRef = useRef(new Map());

  const logNotificationUi = useCallback((stage, fields = {}) => {
    // //console.log(
    //   JSON.stringify({
    //     ns: 'notification-ui',
    //     stage,
    //     ts: new Date().toISOString(),
    //     ...fields,
    //   })
    // );
  }, []);

  const logToastStage = useCallback((stage, fields = {}) => {
    // //console.log(
    //   JSON.stringify({
    //     ns: 'notification-toast',
    //     stage,
    //     ts: new Date().toISOString(),
    //     ...fields,
    //   })
    // );
  }, []);

  const renderRealtimeToastCandidate = useCallback((notification, source = 'unknown') => {
    const notificationType = String(notification?.notificationType || notification?.type || '').toLowerCase();
    const action = String(notification?.action || 'created').toLowerCase();
    const notificationId =
      notification?.notificationId ?? notification?.id ?? notification?.data?.notificationId ?? null;

    logToastStage('toast_candidate_received', {
      source,
      notificationType,
      action,
      notificationId,
    });

    if (CHAT_NOTIFICATION_TYPES.has(notificationType)) {
      logToastStage('toast_filter_skipped', {
        source,
        reason: 'live_chat_removed',
        notificationType,
        action,
        notificationId,
      });
      return;
    }

    if (action !== 'created') {
      logToastStage('toast_filter_skipped', {
        source,
        reason: 'action_not_created',
        notificationType,
        action,
        notificationId,
      });
      return;
    }

    if (!TOAST_ELIGIBLE_NOTIFICATION_TYPES.has(notificationType)) {
      logToastStage('toast_filter_skipped', {
        source,
        reason: 'notification_type_not_eligible',
        notificationType,
        action,
        notificationId,
      });
      return;
    }

    const titleLine = notification?.title || 'Notification';
    const bodyLine = notification?.message || notification?.body || '';
    const queueEvtId =
      notification?.metadata?.queueEventId != null
        ? String(notification.metadata.queueEventId).trim()
        : notification?.data?.queueEventId != null
          ? String(notification.data.queueEventId).trim()
          : '';
    const ticketIdForDedupe = extractTicketIdFromNotif(notification);
    let dedupeKey;
    if (isUnassignedTicketCreationRow(notification) && ticketIdForDedupe != null) {
      dedupeKey = unassignedTicketCreationDedupeKey(ticketIdForDedupe);
    } else if (queueEvtId !== '') {
      dedupeKey = `queue-event:${queueEvtId}`;
    } else if (notificationId != null) {
      dedupeKey = `notification:${notificationId}`;
    } else if (ticketIdForDedupe != null) {
      dedupeKey = `notification-ticket:${ticketIdForDedupe}:${notificationType}`;
    }

    logToastStage('toast_render_triggered', {
      source,
      notificationType,
      action,
      notificationId,
      dedupeKey,
      queueEventId: queueEvtId || undefined,
    });
    showRealtimeNotificationToast(titleLine, bodyLine, {
      dedupeKey,
      notification: buildNotificationNavigationPayload(notification),
    });
    logToastStage('toast_render_completed', {
      source,
      notificationType,
      action,
      notificationId,
    });
  }, [logToastStage]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
    return !!token;
  }, []);

  // Get current user ID
  const getCurrentUserId = useCallback(() => {
    const token = sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
    if (!token) return null;
    const userInfo = extractUserInfo(token);
    return userInfo?.userId || null;
  }, []);

  // Load notifications from localStorage
  const loadNotifications = useCallback(() => {
    try {
      // const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
      const stored = sessionStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const arr = (Array.isArray(parsed) ? parsed : []).filter(
          (n) => !CHAT_NOTIFICATION_TYPES.has(String(n?.notificationType || n?.type || '').toLowerCase())
        );
        setNotifications(arr.slice(0, PANEL_MAX_NOTIFICATIONS));
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    }
  }, []);

  // Save notifications to localStorage
  const saveNotifications = useCallback((notifs) => {
    try {
      const capped = Array.isArray(notifs) ? notifs.slice(0, PANEL_MAX_NOTIFICATIONS) : [];
      sessionStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify(capped));
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }, []);

  const bumpUnreadCount = useCallback((delta = 1) => {
    if (typeof delta !== 'number' || delta === 0) {
      return;
    }
    setUnreadCount((u) => Math.max(0, (typeof u === 'number' ? u : 0) + delta));
  }, []);

  const syncProfileCompletionReminder = useCallback(() => {
    if (!isAuthenticated()) {
      return;
    }

    const userDetails = getStoredUserDetails();
    if (!userDetails) {
      return;
    }

    const phoneMissing = isMobileNumberMissing(userDetails);
    const sessionDismissed = isProfileCompletionReminderDismissed();

    setNotifications((prev) => {
      const withoutReminder = prev.filter((n) => !isProfileCompletionNotification(n));

      if (!phoneMissing || sessionDismissed) {
        if (withoutReminder.length === prev.length) {
          return prev;
        }
        saveNotifications(withoutReminder);
        setUnreadCount(withoutReminder.filter(isUnreadNotificationRow).length);
        return withoutReminder;
      }

      const hasSingleReminder =
        prev.some(isProfileCompletionNotification) &&
        withoutReminder.length + 1 === prev.length;
      if (hasSingleReminder) {
        return prev;
      }

      const reminder = buildProfileCompletionNotification();
      const updated = [reminder, ...withoutReminder].slice(0, PANEL_MAX_NOTIFICATIONS);
      saveNotifications(updated);
      setUnreadCount(updated.filter(isUnreadNotificationRow).length);
      return updated;
    });
  }, [isAuthenticated, saveNotifications]);

  const dismissProfileCompletionReminder = useCallback(() => {
    dismissProfileCompletionReminderForSession();
    setNotifications((prev) => {
      const updated = prev.filter((n) => !isProfileCompletionNotification(n));
      if (updated.length === prev.length) {
        return prev;
      }
      saveNotifications(updated);
      setUnreadCount(updated.filter(isUnreadNotificationRow).length);
      return updated;
    });
  }, [saveNotifications]);

  // Add a new notification
  const addNotification = useCallback((notification) => {
    const incomingType = String(notification?.notificationType || notification?.type || '').toLowerCase();
    if (CHAT_NOTIFICATION_TYPES.has(incomingType)) {
      return;
    }

    const incomingIdRaw = notification.notificationId ?? notification.id;
    const persistedId = resolveNotificationPersistedId(notification);
    const stableLocalId =
      persistedId ??
      (incomingIdRaw != null && String(incomingIdRaw).trim() !== ''
        ? String(incomingIdRaw).trim()
        : `local:${Date.now()}`);
    // Use backend notification structure if available, otherwise create from FCM payload
    const newNotification = {
      id: stableLocalId,
      notificationId: persistedId ?? stableLocalId,
      title: notification.title || 'New Notification',
      body: notification.body || notification.message || '',
      message: notification.message || notification.body || '',
      type: notification.notificationType || notification.type || 'info',
      notificationType: notification.notificationType || notification.type || 'info',
      ticketId: notification.ticketId || notification.metadata?.ticketId || null,
      ticketNumber:
        notification.ticketNumber ||
        notification.metadata?.ticketNumber ||
        notification.data?.ticketNumber ||
        null,
      link: notification.link || null,
      priority: notification.priority || 'normal',
      isRead: notification.isRead || notification.read || false,
      read: notification.read || notification.isRead || false,
      timestamp: notification.createdAt || notification.timestamp || new Date().toISOString(),
      createdAt: notification.createdAt || notification.timestamp || new Date().toISOString(),
      readAt: notification.readAt || null,
      metadata: notification.metadata || notification.data || {},
      data: notification.data || notification.metadata || {},
    };

    const incomingTicketId = extractTicketIdFromNotif(newNotification);
    const incomingIsUnread = isUnreadNotificationRow(newNotification);

    setNotifications(prev => {
      // Check for duplicates by notificationId
      const existingIds = new Set(prev.map(n => String(n.notificationId || n.id)));
      const notifId = String(newNotification.notificationId || newNotification.id);

      if (existingIds.has(notifId)) {
        //console.log("🔄 Skipping duplicate notification:", notifId);
        return prev; // Don't add duplicate
      }

      if (incomingTicketId != null && isUnassignedTicketCreationRow(newNotification)) {
        const dupIdx = prev.findIndex(
          (n) =>
            extractTicketIdFromNotif(n) != null &&
            String(extractTicketIdFromNotif(n)) === String(incomingTicketId) &&
            isUnassignedTicketCreationRow(n)
        );
        if (dupIdx >= 0) {
          if (!preferIncomingUnassignedRow(prev[dupIdx], newNotification)) {
            return prev;
          }
          const withoutDup = prev.filter((_, i) => i !== dupIdx);
          const updated = [newNotification, ...withoutDup].slice(0, PANEL_MAX_NOTIFICATIONS);
          saveNotifications(updated);
          if (incomingIsUnread && !isUnreadNotificationRow(prev[dupIdx])) {
            bumpUnreadCount(1);
          }
          return updated;
        }
      }

      // Add new notification at the beginning (newest first), cap panel size
      const updated = [newNotification, ...prev].slice(0, PANEL_MAX_NOTIFICATIONS);
      saveNotifications(updated);
      if (incomingIsUnread) {
        bumpUnreadCount(1);
      }
      return updated;
    });

    if (incomingTicketId != null && isUnassignedTicketCreationRow(newNotification)) {
      const dedupeKey = unassignedTicketCreationDedupeKey(incomingTicketId);
      if (dedupeKey) {
        const now = Date.now();
        cleanupExpiryMap(bridgeToastRecentByTicketRef.current, now);
        bridgeToastRecentByTicketRef.current.set(dedupeKey, now + 120_000);
        bridgeToastRecentByTicketRef.current.set(`${incomingTicketId}:ticket_created`, now + 120_000);
        bridgeToastRecentByTicketRef.current.set(`${incomingTicketId}:${NEW_UNASSIGNED_TICKET_TYPE}`, now + 120_000);
      }
    }

    if (typeof window !== 'undefined') {
      // Portal ticket modal listens so agent replies / info-requests appear without refresh.
      emitCustomerTicketActivity(newNotification);
    }
  }, [saveNotifications, bumpUnreadCount]);

  const applyWebSocketLifecycle = useCallback(
    (msg) => {
      const action = String(msg?.action || 'created').toLowerCase();
      if (action === 'created' || !action) {
        return false;
      }
      const meta = msg?.metadata || msg?.data || {};
      if (action === 'cleared_all') {
        setNotifications([]);
        const u = meta.unreadCount;
        setUnreadCount(typeof u === 'number' ? u : 0);
        try {
          sessionStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        return true;
      }
      if (action === 'read_all') {
        setNotifications((prev) => {
          const next = prev.map((n) => ({ ...n, read: true, isRead: true }));
          saveNotifications(next);
          return next;
        });
        const u = meta.unreadCount;
        if (typeof u === 'number') {
          setUnreadCount(u);
        } else {
          getUnreadCount()
            .then((cr) => {
              if (cr.success && typeof cr.data === 'number') {
                setUnreadCount(cr.data);
              }
            })
            .catch(() => {});
        }
        return true;
      }
      if (action === 'read') {
        const nid = msg.notificationId ?? meta.notificationId;
        if (nid == null) {
          return true;
        }
        setNotifications((prev) => {
          const next = prev.map((n) =>
            String(n.notificationId || n.id) === String(nid) ? { ...n, read: true, isRead: true } : n
          );
          saveNotifications(next);
          return next;
        });
        return true;
      }
      if (action === 'deleted') {
        const nid = msg.notificationId ?? meta.notificationId;
        if (nid == null) {
          return true;
        }
        setNotifications((prev) => {
          const next = prev.filter((n) => String(n.notificationId || n.id) !== String(nid));
          saveNotifications(next);
          return next;
        });
        return true;
      }
      return false;
    },
    [saveNotifications]
  );

  // Fetch notifications from backend API
  const fetchNotificationsFromBackend = useCallback(async (unreadOnly = false, options = {}) => {
    if (!isAuthenticated()) {
      //console.log("User not authenticated, skipping backend notification fetch");
      return;
    }
    const toastOnNew = Boolean(options?.toastOnNew);
    const toastSource = options?.toastSource || 'notification_fetch';

    try {
      setIsLoading(true);
      const response = await getNotifications(0, PANEL_MAX_NOTIFICATIONS, unreadOnly);

      if (response.success && response.data?.content) {
        // Map backend notification format to frontend format
        const mappedNotifications = response.data.content.filter((backendNotif) => {
          const nt = String(backendNotif.notificationType || '').toLowerCase();
          return !CHAT_NOTIFICATION_TYPES.has(nt);
        }).map(backendNotif => ({
          id: backendNotif.notificationId,
          notificationId: backendNotif.notificationId,
          title: backendNotif.title || 'Notification',
          body: backendNotif.message || '',
          message: backendNotif.message || '',
          type: backendNotif.notificationType || 'info',
          notificationType: backendNotif.notificationType || 'info',
          ticketId: backendNotif.metadata?.ticketId || backendNotif.link?.match(/\/tickets\/(\d+)/)?.[1] || null,
          ticketNumber: backendNotif.metadata?.ticketNumber || null,
          link: backendNotif.link,
          priority: backendNotif.priority || 'normal',
          isRead: backendNotif.isRead || false,
          read: backendNotif.isRead || false,
          timestamp: backendNotif.createdAt || new Date().toISOString(),
          createdAt: backendNotif.createdAt,
          readAt: backendNotif.readAt,
          metadata: backendNotif.metadata || {},
          data: backendNotif.metadata || {},
        }));

        let newNotificationsForToast = [];
        const nowMs = Date.now();
        cleanupExpiryMap(bridgeToastRecentByTicketRef.current, nowMs);

        setNotifications((prevNotifications) => {
          const merged = mergeMappedWithQueueSynthetics(
            prevNotifications,
            mappedNotifications,
            PANEL_MAX_NOTIFICATIONS
          );
          const existingIds = new Set(prevNotifications.map((n) => String(n.notificationId || n.id)));
          newNotificationsForToast = merged.filter((n) => {
            const notifId = String(n.notificationId || n.id);
            if (existingIds.has(notifId)) {
              //console.log(
              //   "🔄 Skipping duplicate notification from backend:",
              //   notifId,
              //   "- already received via realtime"
              // );
              return false;
            }
            return true;
          });

          saveNotifications(merged);
          logNotificationUi('bell_merge_api_with_synthetics', {
            source: toastSource,
            apiCount: mappedNotifications.length,
            mergedCount: merged.length,
          });
          return merged;
        });

        if (toastOnNew && newNotificationsForToast.length > 0) {
          newNotificationsForToast.forEach((n) => {
            const tid = extractTicketIdFromNotif(n);
            const nt = String(n.notificationType || n.type || '').toLowerCase();
            const bridgeKey = tid != null ? `${tid}:${nt}` : null;
            const unassignedKey =
              tid != null && UNASSIGNED_TICKET_CREATION_TYPES.has(nt)
                ? unassignedTicketCreationDedupeKey(tid)
                : null;
            const exp =
              (unassignedKey != null ? bridgeToastRecentByTicketRef.current.get(unassignedKey) : null) ??
              (bridgeKey != null ? bridgeToastRecentByTicketRef.current.get(bridgeKey) : null);
            if (
              (unassignedKey != null || bridgeKey != null) &&
              typeof exp === 'number' &&
              exp > nowMs
            ) {
              logToastStage('toast_filter_skipped', {
                source: toastSource,
                reason: 'recent_queue_bridge_toast',
                notificationId: n.notificationId,
                ticketId: tid,
                notificationType: nt,
              });
              return;
            }
            renderRealtimeToastCandidate(n, toastSource);
            emitCustomerTicketActivity(n);
          });
        }

        setLastSyncTime(new Date().toISOString());

        // Update unread count from backend
        try {
          const countResponse = await getUnreadCount();
          if (countResponse.success && typeof countResponse.data === 'number') {
            setUnreadCount((prev) =>
              Math.max(typeof prev === 'number' ? prev : 0, countResponse.data)
            );
          }
        } catch (countError) {
          console.warn("Failed to fetch unread count:", countError);
          const localCount = mappedNotifications.filter((n) => isUnreadNotificationRow(n)).length;
          setUnreadCount((prev) => Math.max(typeof prev === 'number' ? prev : 0, localCount));
        }

        //console.log("✅ Notifications fetched from backend:", mappedNotifications.length);
      }

      syncProfileCompletionReminder();
    } catch (error) {
      console.error("Error fetching notifications from backend:", error);
      // Fallback to localStorage on error
      loadNotifications();
      syncProfileCompletionReminder();
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, loadNotifications, saveNotifications, renderRealtimeToastCandidate, logNotificationUi, logToastStage, syncProfileCompletionReminder]);

  useEffect(() => {
    const onQueueBridge = (event) => {
      if (!isAuthenticated()) {
        return;
      }
      const notification = event?.detail?.notification;
      if (!notification) {
        return;
      }
      const qe =
        notification.metadata?.queueEventId != null
          ? String(notification.metadata.queueEventId).trim()
          : '';
      const now = Date.now();
      if (qe !== '') {
        cleanupExpiryMap(processedQueueBridgeEventIdsRef.current, now);
        const exp = processedQueueBridgeEventIdsRef.current.get(qe);
        if (typeof exp === 'number' && exp > now) {
          logNotificationUi('queue_bridge_duplicate_delivery_suppressed', {
            queueEventId: qe,
            notificationType: notification.notificationType,
          });
          return;
        }
        processedQueueBridgeEventIdsRef.current.set(qe, now + 120_000);
      }

      logNotificationUi('queue_bridge_handler_enter', {
        queueEventId: qe || undefined,
        ticketId: notification.ticketId,
        notificationType: notification.notificationType,
      });

      if (applyWebSocketLifecycle(notification)) {
        logNotificationUi('queue_bridge_short_circuit_lifecycle', { queueEventId: qe || undefined });
        return;
      }

      addNotification(notification);

      logNotificationUi('bell_update', {
        source: 'queue_realtime_bridge',
        queueEventId: qe || undefined,
        notificationType: notification.notificationType,
      });

      const tid = extractTicketIdFromNotif(notification);
      const nt = String(notification.notificationType || '').toLowerCase();
      if (tid != null && nt !== '') {
        cleanupExpiryMap(bridgeToastRecentByTicketRef.current, now);
        bridgeToastRecentByTicketRef.current.set(`${tid}:${nt}`, now + 22_000);
      }

      renderRealtimeToastCandidate(notification, 'queue_realtime_bridge');
    };

    window.addEventListener(QUEUE_REALTIME_NOTIFICATION_UI_EVENT, onQueueBridge);
    return () => {
      window.removeEventListener(QUEUE_REALTIME_NOTIFICATION_UI_EVENT, onQueueBridge);
    };
  }, [isAuthenticated, addNotification, applyWebSocketLifecycle, renderRealtimeToastCandidate, logNotificationUi]);

  const dismissAllFromPanel = useCallback(async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      sessionStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if (!isAuthenticated()) {
      return;
    }
    try {
      const res = await dismissAllNotifications();
      if (res.success && res.data && typeof res.data.unreadCount === 'number') {
        setUnreadCount(res.data.unreadCount);
      } else {
        const countResponse = await getUnreadCount();
        if (countResponse.success && typeof countResponse.data === 'number') {
          setUnreadCount(countResponse.data);
        }
      }
    } catch (error) {
      console.warn('Failed to dismiss all notifications on server:', error);
      fetchNotificationsFromBackend(false);
    }
  }, [isAuthenticated, fetchNotificationsFromBackend]);

  const scheduleNotificationRefresh = useCallback((reason = 'unspecified') => {
    if (!isAuthenticated()) {
      return;
    }
    if (notificationRefreshTimerRef.current) {
      clearTimeout(notificationRefreshTimerRef.current);
      notificationRefreshTimerRef.current = null;
      //console.log(
      //   JSON.stringify({
      //     ns: 'notification-realtime',
      //     stage: 'refresh_debounce_suppressed',
      //     reason,
      //     ts: new Date().toISOString(),
      //   })
      // );
    }
    notificationRefreshTimerRef.current = setTimeout(() => {
      notificationRefreshTimerRef.current = null;
      //console.log(
      //   JSON.stringify({
      //     ns: 'notification-realtime',
      //     stage: 'refresh_execute',
      //     reason,
      //     ts: new Date().toISOString(),
      //   })
      // );
      fetchNotificationsFromBackend(false, {
        toastOnNew: true,
        toastSource: `queue_refresh:${reason}`,
      });
    }, NOTIFICATION_REALTIME_DEBOUNCE_MS);
  }, [isAuthenticated, fetchNotificationsFromBackend]);

  // Ref to the single notification handler so we can replace it without clearing other subscribers
  const wsNotificationHandlerRef = useRef(null);

  // Setup WebSocket notification subscription
  const setupWebSocketNotifications = useCallback(() => {
    if (!isAuthenticated()) {
      return;
    }

    const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');

    if (!token) {
      console.warn('No token available for WebSocket notification subscription');
      return;
    }

    if (websocketService.getIsConnected()) {
      if (wsNotificationHandlerRef.current) {
        websocketService.unsubscribeFromNotificationsCallback(wsNotificationHandlerRef.current);
        wsNotificationHandlerRef.current = null;
      }

      //console.log('🔔 Subscribing to WebSocket notifications...');

      const handler = (notification) => {
        //console.log('🔔 WebSocket notification received:', notification);
        logNotificationUi('websocket_notification_inbound', {
          channel: 'subscribeToNotifications',
          notificationType: notification.notificationType || notification.type,
          action: notification.action,
          notificationId: notification.notificationId ?? notification.id,
        });

        if (applyWebSocketLifecycle(notification)) {
          logNotificationUi('websocket_notification_short_circuit', { reason: 'lifecycle_action' });
          return;
        }

        addNotification(notification);
        logNotificationUi('bell_update', {
          source: 'websocket_notification',
          notificationType: notification.notificationType || notification.type,
        });

        renderRealtimeToastCandidate(notification, 'websocket_notification');
      };

      wsNotificationHandlerRef.current = handler;
      websocketService.subscribeToNotifications(handler);
    } else {
      console.warn('⚠️ WebSocket not connected yet (NotificationContext)');
    }
  }, [isAuthenticated, addNotification, renderRealtimeToastCandidate, applyWebSocketLifecycle, logNotificationUi]);

  // When the agent notification WebSocket becomes ready, subscribe (avoids race with initial mount)
  useEffect(() => {
    if (!wsConnected || !isAuthenticated()) {
      return;
    }
    setupWebSocketNotifications();
  }, [wsConnected, isAuthenticated, setupWebSocketNotifications]);

  // Cleanup WebSocket subscription
  const cleanupWebSocketNotifications = useCallback(() => {
    if (wsNotificationHandlerRef.current) {
      websocketService.unsubscribeFromNotificationsCallback(wsNotificationHandlerRef.current);
      wsNotificationHandlerRef.current = null;
    }
    processedQueueBridgeEventIdsRef.current.clear();
    bridgeToastRecentByTicketRef.current.clear();
    clearRealtimeNotificationToastQueue();
  }, []);

  // Load notifications from localStorage on mount
  useEffect(() => {
    // First load from localStorage (immediate display)
    loadNotifications();

    // Then fetch from backend if authenticated (on initial mount)
    if (isAuthenticated()) {
      //console.log("🔔 User authenticated on mount - fetching notifications from backend");
      fetchNotificationsFromBackend(false);

      // Setup WebSocket notifications after a short delay to ensure token is available
      setTimeout(() => {
        setupWebSocketNotifications();
      }, 500);
    }

    // Listen for custom notification events from MessagingService (FCM real-time notifications)
    // This handler adds notifications instantly from FCM payload without API calls
    const handleNewNotification = (event) => {
      if (event.detail) {
        addNotification(event.detail);
        //console.log("✅ Real-time notification added from FCM:", event.detail);
      }
    };

    window.addEventListener('fcm-notification', handleNewNotification);

    const handleUserDetailsUpdated = () => {
      syncProfileCompletionReminder();
    };
    window.addEventListener('hd:user-details-updated', handleUserDetailsUpdated);

    return () => {
      if (notificationRefreshTimerRef.current) {
        clearTimeout(notificationRefreshTimerRef.current);
        notificationRefreshTimerRef.current = null;
      }
      window.removeEventListener('fcm-notification', handleNewNotification);
      window.removeEventListener('hd:user-details-updated', handleUserDetailsUpdated);
      cleanupWebSocketNotifications();
    };
  }, [isAuthenticated, fetchNotificationsFromBackend, loadNotifications, addNotification, setupWebSocketNotifications, cleanupWebSocketNotifications, syncProfileCompletionReminder]);

  useEffect(() => {
    const handleQueueRealtimeGlobalEvent = (event) => {
      const reason = event?.detail?.eventType || 'queue_realtime_global_event';
      //console.log(
      //   JSON.stringify({
      //     ns: 'notification-realtime',
      //     stage: 'queue_realtime_event_received',
      //     reason,
      //     ts: new Date().toISOString(),
      //   })
      // );
      scheduleNotificationRefresh(reason);
    };

    window.addEventListener('hd:queue-realtime-global-event', handleQueueRealtimeGlobalEvent);
    return () => window.removeEventListener('hd:queue-realtime-global-event', handleQueueRealtimeGlobalEvent);
  }, [scheduleNotificationRefresh]);

  // Watch for authentication changes (login/logout) and fetch notifications
  useEffect(() => {
    // Listen for custom login event - triggered when user logs in
    const handleLogin = () => {
      //console.log("🔔 Login event detected - fetching notifications from backend");
      // Small delay to ensure token is fully set in storage
      setTimeout(() => {
        if (isAuthenticated()) {
          fetchNotificationsFromBackend(false);
          // Setup WebSocket notifications after login
          setupWebSocketNotifications();
          setTimeout(() => {
            syncProfileCompletionReminder();
          }, 900);
        } else {
          console.warn("⚠️ Login event received but user not authenticated yet");
        }
      }, 500); // Give time for token to be set
    };

    window.addEventListener('user-logged-in', handleLogin);

    // Listen for storage changes (cross-tab login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'authToken') {
        //console.log("🔔 Token storage changed - checking authentication and fetching notifications");
        setTimeout(() => {
          if (isAuthenticated()) {
            fetchNotificationsFromBackend(false);
            // Setup WebSocket notifications
            setupWebSocketNotifications();
          } else {
            // User logged out - clear notifications and cleanup WebSocket
            setNotifications([]);
            setUnreadCount(0);
            cleanupWebSocketNotifications();
          }
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('user-logged-in', handleLogin);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, fetchNotificationsFromBackend, setupWebSocketNotifications, cleanupWebSocketNotifications, syncProfileCompletionReminder]);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId) => {
    // Update local state immediately (optimistic update)
    setNotifications(prev => {
      const updated = prev.map(n =>
        n.id === notificationId || n.notificationId === notificationId
          ? { ...n, read: true, isRead: true }
          : n
      );
      saveNotifications(updated);
      return updated;
    });

    if (!isAuthenticated() || !notificationId) {
      return;
    }

    const matchedRow = notifications.find(
      (n) => n.id === notificationId || n.notificationId === notificationId
    );
    const backendNotificationId =
      [matchedRow?.notificationId, notificationId].find(isBackendPersistedNotificationId) ?? null;

    if (!backendNotificationId) {
      return;
    }

    try {
      await markNotificationAsRead(backendNotificationId);
      //console.log("✅ Notification marked as read in backend");

      // Refresh unread count
      const countResponse = await getUnreadCount();
      if (countResponse.success && typeof countResponse.data === 'number') {
        setUnreadCount(countResponse.data);
      }
    } catch (error) {
      console.warn("Failed to mark notification as read in backend:", error);
      // Local state already updated, continue
    }
  }, [saveNotifications, isAuthenticated, notifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Update local state immediately (optimistic update)
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true, isRead: true }));
      saveNotifications(updated);
      return updated;
    });

    // Sync with backend if authenticated
    if (isAuthenticated()) {
      try {
        await markAllNotificationsAsRead();
        //console.log("✅ All notifications marked as read in backend");

        // Refresh unread count
        const countResponse = await getUnreadCount();
        if (countResponse.success && typeof countResponse.data === 'number') {
          setUnreadCount(countResponse.data);
        }
      } catch (error) {
        console.warn("Failed to mark all notifications as read in backend:", error);
        // Local state already updated, continue
      }
    }
  }, [saveNotifications, isAuthenticated]);

  // Remove a notification
  const removeNotification = useCallback(async (notificationId) => {
    // Update local state immediately (optimistic update)
    setNotifications(prev => {
      const updated = prev.filter(n =>
        n.id !== notificationId && n.notificationId !== notificationId
      );
      saveNotifications(updated);
      return updated;
    });

    // Sync with backend if authenticated
    if (isAuthenticated() && notificationId) {
      try {
        // Use notificationId from backend if available, otherwise use id
        const backendNotificationId = typeof notificationId === 'number'
          ? notificationId
          : (notifications.find(n => n.id === notificationId)?.notificationId || notificationId);

        await deleteNotification(backendNotificationId);
        //console.log("✅ Notification deleted in backend");

        // Refresh unread count
        const countResponse = await getUnreadCount();
        if (countResponse.success && typeof countResponse.data === 'number') {
          setUnreadCount(countResponse.data);
        }
      } catch (error) {
        console.warn("Failed to delete notification in backend:", error);
        // Local state already updated, continue
      }
    }
  }, [saveNotifications, isAuthenticated, notifications]);

  // Clear all notifications (used on logout)
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    sessionStorage.removeItem(NOTIFICATIONS_STORAGE_KEY);
    processedQueueBridgeEventIdsRef.current.clear();
    bridgeToastRecentByTicketRef.current.clear();
    clearRealtimeNotificationToastQueue();
  }, []);

  useEffect(() => {
    registerNotificationLogoutHandlers({ clearAllNotifications });
    return () => registerNotificationLogoutHandlers(null);
  }, [clearAllNotifications]);

  // Get notification icon type based on notification type
  const getNotificationIcon = useCallback((type) => {
    switch (type) {
      case 'ticket_assigned':
      case 'assigned':
        return 'info';
      case 'ticket_reply':
        return 'info';
      case 'sla_warning':
      case 'sla_breach':
        return 'warning';
      case 'ticket_resolved':
      case 'resolved':
        return 'success';
      case 'ticket_escalated':
      case 'escalated':
        return 'error';
      case 'profile_completion':
        return 'warning';
      default:
        return 'info';
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    lastSyncTime,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissAllFromPanel,
    removeNotification,
    clearAllNotifications,
    loadNotifications,
    fetchNotificationsFromBackend,
    getNotificationIcon,
    getCurrentUserId,
    dismissProfileCompletionReminder,
    syncProfileCompletionReminder,
  };

  return (
    <NotificationContext.Provider value={value}>
      <NotificationNavigationRegistrar />
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;

